'use client';

import React, { useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { SuccessIcon, ErrorIcon, WarningIcon, InfoIcon, CloseIcon } from '@/components/icons/ToastIcons';

/**
 * Toast Props
 */
export interface ToastProps {
  /** Toast message */
  message: string;
  /** Toast type */
  type?: 'success' | 'error' | 'warning' | 'info';
  /** Duration in milliseconds (0 means persistent) */
  duration?: number;
  /** Callback when toast closes */
  onClose?: () => void;
  /** Custom className */
  className?: string;
  /** Show close button */
  showCloseButton?: boolean;
}

// ============================================================================
// Animations
// ============================================================================

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

const ToastContainer = styled.div<{ $type: 'success' | 'error' | 'warning' | 'info'; $isExiting?: boolean }>`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 9999;
  min-width: 300px;
  max-width: 500px;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  animation: ${({ $isExiting }) => 
    $isExiting 
      ? css`${slideOut} 0.3s ease-out forwards`
      : css`${slideIn} 0.3s ease-out`
  };
  
  background-color: ${({ $type }) => {
    switch ($type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  }};
  
  color: #ffffff;
  
  @media (max-width: 640px) {
    top: 16px;
    right: 16px;
    left: 16px;
    min-width: auto;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Message = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
  flex-shrink: 0;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// ============================================================================
// Component
// ============================================================================

/**
 * Toast Component
 * 
 * A notification component that displays temporary messages.
 * 
 * @component
 * @example
 * ```tsx
 * <Toast 
 *   message="Pattern deleted successfully" 
 *   type="success" 
 *   duration={3000}
 *   onClose={() => console.log('closed')}
 * />
 * ```
 */
export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 3000,
  onClose,
  className,
  showCloseButton = true,
}) => {
  const [isExiting, setIsExiting] = React.useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose?.();
    }, 300); // Match animation duration
  };

  const getIcon = () => {
    switch (type) {
      case 'success': return <SuccessIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <ToastContainer $type={type} $isExiting={isExiting} className={className}>
      <IconWrapper>{getIcon()}</IconWrapper>
      <Message>{message}</Message>
      {showCloseButton && (
        <CloseButton onClick={handleClose} aria-label="Close notification">
          <CloseIcon />
        </CloseButton>
      )}
    </ToastContainer>
  );
};

export default Toast;

